import { Stack, Typography, Chip } from '@mui/material';
import { Flags } from '@watershed/constants/flags';

import {
  GQActiveMeasurementProjectForOnboardingHomePageFragment,
  GQClimateProgramTimelineProjectFieldsFragment,
} from '@watershed/app-dashboard/generated/graphql-operations';
import {
  GQClimateProgramProjectKind,
  GQMeasurementProjectFootprintStatus,
  GQTaskWatershedProcessStateSimplified,
} from '@watershed/app-dashboard/generated/graphql-schema-types';

import { Trans } from '@lingui/react/macro';
import ArrowRightIcon from '@watershed/icons/components/ArrowRight';
import CircleCheckmarkFillIcon from '@watershed/icons/components/CircleCheckmarkFill';
import {
  routeForSsoConfig,
  routeForOrganizationUsers,
  routeForNewMeasurement,
  routeForClimateProgramTimeline,
  routeForMeasurementProject,
  routeForFootprintOverview,
  routeForOrgStructure,
} from '@watershed/shared-universal/dashboardRoutes';

import BoxLink from '@watershed/ui-core/components/BoxLink';
import TextLink from '@watershed/ui-core/components/TextLink';
import groupBy from 'lodash/groupBy';
import { useUserContext } from '../../utils/UserContext';
import { getCtaInfoForTimelineItem } from '../climate-program/CtaHelpers';
import { MEASUREMENT_TASK_STATUSES_SIMPLIFIED } from '../measure/active/useMeasurementProjectFilters';
import { getGqlResultData } from '@watershed/shared-frontend/utils/errorUtils';
import flattenConnection from '@watershed/shared-universal/utils/flattenConnection';
import { useOnboardingNavCardQuery } from '@watershed/app-dashboard/generated/urql';
import { isTaskComplete } from '@watershed/shared-frontend/components/measurement/measurementTaskUtils';
import { useFeatureFlag } from '../../utils/FeatureFlag';

// TODO: Move NavCard and NavCardLink to their own files and add to storybook.
function NavCard({
  header,
  ctaInfo,
  children,
}: {
  header: React.ReactNode;
  ctaInfo?: {
    label: React.ReactNode;
    href: string;
  };
  children?:
    | Array<React.ReactElement<NavCardLinkProps> | null | undefined>
    | React.ReactElement<NavCardLinkProps>;
}) {
  return (
    <Stack
      borderRadius={1}
      bgcolor={(theme) => theme.palette.paper}
      border={(theme) => `1px solid ${theme.palette.border}`}
      width={600}
    >
      <Stack direction="row" padding={2} gap={1}>
        <Typography variant="h2">{header}</Typography>
        {ctaInfo && (
          <TextLink
            sx={{ marginLeft: 'auto' }}
            endIcon={<ArrowRightIcon />}
            href={ctaInfo.href}
          >
            {ctaInfo?.label}
          </TextLink>
        )}
      </Stack>
      {children}
    </Stack>
  );
}

type NavCardLinkProps = {
  text: string;
  href: string;
  isCompleted?: boolean;
  isOptional?: boolean;
};
function NavCardLink({
  text,
  href,
  isCompleted = false,
  isOptional = false,
}: NavCardLinkProps) {
  return (
    <BoxLink
      href={href}
      width={1}
      padding={2}
      borderTop={(theme) => `1px solid ${theme.palette.border}`}
      sx={{
        ':hover': {
          backgroundColor: (theme) => theme.palette.primary.light,
        },
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        width="100%"
      >
        <Stack direction="row" alignItems="center" gap={1}>
          <Typography>{text}</Typography>
          {isOptional && (
            <Chip label={<Trans context="label on chip">Optional</Trans>} />
          )}
        </Stack>
        {isCompleted ? (
          <CircleCheckmarkFillIcon
            color={(theme) => theme.palette.success.main}
          />
        ) : (
          <ArrowRightIcon color={(theme) => theme.palette.primary.main} />
        )}
      </Stack>
    </BoxLink>
  );
}

export function OnboardingNavCard() {
  const [result] = useOnboardingNavCardQuery();
  const data = getGqlResultData(result);
  const userCount = flattenConnection(data?.organization.users).length;
  const canAccessOrgStructure = useFeatureFlag(Flags.OrgStructure);
  return (
    <NavCard
      header={
        <Trans context="Nav card header with onboard as a verb to get onboarded">
          Onboard
        </Trans>
      }
    >
      <NavCardLink
        text="Set up SSO via WorkOS"
        href={routeForSsoConfig()}
        isOptional
        isCompleted={data?.organization.hasActiveWorkosConnection}
      />
      <NavCardLink
        text="Invite your team"
        href={routeForOrganizationUsers()}
        isOptional
        isCompleted={userCount > 1}
      />
      {canAccessOrgStructure ? (
        <NavCardLink
          text="Configure your organizational structure"
          href={routeForOrgStructure()}
        />
      ) : null}
      <NavCardLink
        text="Set up your first measurement"
        href={routeForNewMeasurement()}
      />
    </NavCard>
  );
}

export function StartNewMeasureNavCard() {
  return (
    <NavCard
      header={
        <Trans context="Nav card header with Measure as a verb">Measure</Trans>
      }
      ctaInfo={{
        label: 'Start new',
        href: routeForNewMeasurement(),
      }}
    ></NavCard>
  );
}

export function ActiveMeasureNavCard({
  measurementProject,
}: {
  measurementProject: GQActiveMeasurementProjectForOnboardingHomePageFragment;
}) {
  const { userId } = useUserContext();
  const allTasksCta = {
    label: 'All tasks',
    href: routeForMeasurementProject(measurementProject.id, {
      filters: { groupBy: 'taskStatus', assignees: { type: 'all' } },
    }),
  };
  if (
    measurementProject.footprintStatus ===
    GQMeasurementProjectFootprintStatus.Draft
  ) {
    const projectName = measurementProject.name;
    return (
      <NavCard
        header={<Trans context="Nav card header">Measure: {projectName}</Trans>}
        ctaInfo={allTasksCta}
      >
        <NavCardLink
          text="Review draft footprint"
          href={routeForFootprintOverview()}
        />
      </NavCard>
    );
  }

  // We widen the type a bit because otherwise the indexOf complains
  const taskStatuses: ReadonlyArray<GQTaskWatershedProcessStateSimplified> =
    MEASUREMENT_TASK_STATUSES_SIMPLIFIED;

  const tasksToShow = measurementProject.measurementTasks
    .filter((task) =>
      task.assignees.find((assignees) => assignees.id === userId)
    )
    .sort((task) => taskStatuses.indexOf(task.statusSimplified))
    .slice(0, 3);
  const projectName = measurementProject.name;
  return (
    <NavCard
      header={<Trans context="nav card header">Measure: {projectName}</Trans>}
      ctaInfo={allTasksCta}
    >
      {tasksToShow.map((task) =>
        task.link ? (
          <NavCardLink
            key={task.id}
            text={task.title}
            href={task.link}
            isCompleted={isTaskComplete(task)}
          />
        ) : null
      )}
    </NavCard>
  );
}

export function StrategizeNavCard({
  climateProgramTimelineProjects,
}: {
  climateProgramTimelineProjects: Array<GQClimateProgramTimelineProjectFieldsFragment>;
}) {
  const projectsToShow = Object.values(
    groupBy(
      climateProgramTimelineProjects.filter(
        (cptp) => cptp.kind === GQClimateProgramProjectKind.Target
      ),
      (cptp) => cptp.canonicalClimateProgramProject?.id
    )
  ).map((cptps) => cptps[0]);
  return (
    <NavCard
      header={
        <Trans context="nav card header with Strategize as a verb">
          Strategize
        </Trans>
      }
      ctaInfo={{
        label: 'Program timeline',
        href: routeForClimateProgramTimeline(),
      }}
    >
      {projectsToShow.map((cptp) => {
        const ctaInfo = getCtaInfoForTimelineItem(cptp);
        return ctaInfo?.href ? (
          <NavCardLink
            key={cptp.id}
            text={cptp.name}
            isCompleted={!!cptp.completedAt}
            href={ctaInfo.href}
          />
        ) : null;
      })}
    </NavCard>
  );
}
